import { useWorkspaceQuery } from "@/hooks/use-workspace-query";
import type { Id, AuditId } from "@/packages/types";
import { api } from "convex/_generated/api";
import { FileText, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface ReportActionCardProps {
  auditId: AuditId;
  onNavigateToReport: () => void;
  index?: number;
}

export function ReportActionCard({
  auditId,
  onNavigateToReport,
  index,
}: ReportActionCardProps) {
  const { data: report } = useWorkspaceQuery(api.report_queries.getByAudit, {
    auditId: auditId as Id<"audits">,
  });

  // Has report
  if (report) {
    return (
      <WidgetCard
        size="S"
        title="Report"
        icon={<FileText size={16} />}
        onClick={onNavigateToReport}
        index={index}
      >
        <div className="flex flex-col justify-between h-full">
          <div className="flex items-center gap-2">
            <CheckCircle size={20} className="text-emerald-400 shrink-0" />
            <span className="text-sm text-secondary">
              v{report.version} &middot; {report.status}
            </span>
          </div>
          <span className="text-xs text-muted">
            Generated {new Date(report.generatedAt).toLocaleDateString()}
          </span>
          <button
            type="button"
            className="text-xs font-medium text-accent hover:text-accent/80 text-left"
            onClick={(e) => {
              e.stopPropagation();
              onNavigateToReport();
            }}
          >
            View Report
          </button>
        </div>
      </WidgetCard>
    );
  }

  // No report — designed empty state
  return (
    <WidgetCard
      size="S"
      title="Report"
      icon={<FileText size={16} />}
      index={index}
    >
      <motion.div
        className="flex flex-col items-center justify-center h-full gap-1.5 text-center"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
      >
        <div className="w-16 h-16 rounded-full border-2 border-dashed border-border/60 flex items-center justify-center">
          <FileText size={28} className="text-muted" />
        </div>
        <span className="text-sm text-secondary mt-1">No report yet</span>
        <Button
          variant="secondary"
          size="sm"
          className="mt-1 bg-accent/10 text-accent border border-accent/20 hover:bg-accent/15"
          onClick={onNavigateToReport}
        >
          Create Report
        </Button>
      </motion.div>
    </WidgetCard>
  );
}

export function ReportActionCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="S" index={index} />;
}
